import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  artifactService,
  composeArtifactTitle,
  updateAIMessage,
  useAppDispatch,
  type Message,
  type MessageArtifact,
} from '@ideoz/core';
import { renderArtifactHtml, showToast } from '@ideoz/ui';
import { resolveArtifact } from '../../artifacts/renderers/registry';

/** One open artifact = one tab in the panel. */
export type ArtifactTab = {
  /** Stable key for the tab, assigned at open time (survives the id/content rewrite). */
  key: string;
  artifact: MessageArtifact;
  /** Owning message id — the stable anchor for re-syncing after the DB row lands. */
  sourceMessageId: string | null;
};

/**
 * Owns the SSE-streamed artifact side panel. Opening an artifact adds a tab;
 * opening another keeps the first open and adds a second, so the user can switch
 * between several artifacts (Manus-style tabs). The active tab drives the
 * rendered body + the Edit / Share / Save actions.
 *
 * @param messages              rendered messages (drives the async-sync effect)
 * @param conversationMessages  redux conversation messages (save target lookup)
 */
export function useStreamedArtifactPanel(params: {
  messages: Message[];
  conversationMessages: Message[];
}) {
  const { messages, conversationMessages } = params;
  const dispatch = useAppDispatch();

  const [tabs, setTabs] = useState<ArtifactTab[]>([]);
  const [activeTabKey, setActiveTabKey] = useState<string | null>(null);

  // Live handles so the callbacks don't need to be re-created every render.
  const tabsRef = useRef<ArtifactTab[]>(tabs);
  tabsRef.current = tabs;
  const activeTabKeyRef = useRef<string | null>(activeTabKey);
  activeTabKeyRef.current = activeTabKey;
  const keyCounterRef = useRef(0);
  const messagesRef = useRef<Message[]>(messages);
  messagesRef.current = messages;

  const activeArtifact = useMemo(
    () => tabs.find((t) => t.key === activeTabKey)?.artifact ?? null,
    [tabs, activeTabKey]
  );
  const isOpen = tabs.length > 0;

  /** Apply an update to the active tab's artifact (no-op when nothing is active). */
  const updateActiveArtifact = useCallback(
    (updater: (prev: MessageArtifact) => MessageArtifact) => {
      const key = activeTabKeyRef.current;
      if (!key) return;
      setTabs((prev) =>
        prev.map((t) => (t.key === key ? { ...t, artifact: updater(t.artifact) } : t))
      );
    },
    []
  );

  /** Locate the message an artifact belongs to so future updates re-sync. */
  const findSourceId = useCallback((artifact: MessageArtifact): string | null => {
    const source = messagesRef.current.find((m) => {
      const art = m.artifact;
      if (!art) return false;
      if (artifact.id && art.id) return art.id === artifact.id;
      return art.name === artifact.name && art.content === artifact.content;
    });
    return source?.id ?? null;
  }, []);

  /** Open an artifact: activate its existing tab, or add a new one. */
  const open = useCallback(
    (artifact: MessageArtifact) => {
      const sourceId = findSourceId(artifact);
      const existing = tabsRef.current.find(
        (t) =>
          (!!artifact.id && !!t.artifact.id && t.artifact.id === artifact.id) ||
          (!!sourceId && !!t.sourceMessageId && t.sourceMessageId === sourceId) ||
          (t.artifact.name === artifact.name && t.artifact.content === artifact.content)
      );
      if (existing) {
        setTabs((prev) =>
          prev.map((t) => (t.key === existing.key ? { ...t, artifact } : t))
        );
        setActiveTabKey(existing.key);
        return;
      }
      const key = `tab-${keyCounterRef.current++}`;
      setTabs((prev) => [...prev, { key, artifact, sourceMessageId: sourceId }]);
      setActiveTabKey(key);
    },
    [findSourceId]
  );

  /** Close the whole panel (all tabs). */
  const close = useCallback(() => {
    setTabs([]);
    setActiveTabKey(null);
  }, []);

  /** Close one tab; activate a neighbour, or close the panel when it was the last. */
  const closeTab = useCallback((key: string) => {
    const prev = tabsRef.current;
    const idx = prev.findIndex((t) => t.key === key);
    if (idx === -1) return;
    const next = prev.filter((t) => t.key !== key);
    setTabs(next);
    setActiveTabKey((curr) => {
      if (curr !== key) return curr;
      if (next.length === 0) return null;
      return next[Math.min(idx, next.length - 1)].key;
    });
  }, []);

  const selectTab = useCallback((key: string) => setActiveTabKey(key), []);

  /**
   * Keep each open tab in sync with its underlying message as the artifact
   * updates asynchronously (the DB row — and thus `id` / final content — lands
   * seconds after the stream ends via the Celery `persist_from_stream` task).
   * Matching by the captured source-message id survives the name/content rewrite.
   */
  useEffect(() => {
    if (tabs.length === 0) return;
    let changed = false;
    const nextTabs = tabs.map((tab) => {
      const match = messages.find((m) => {
        const art = m.artifact;
        if (!art) return false;
        if (tab.sourceMessageId) return m.id === tab.sourceMessageId;
        if (tab.artifact.id && art.id) return art.id === tab.artifact.id;
        return art.name === tab.artifact.name && art.content === tab.artifact.content;
      });
      const nextArtifact = match?.artifact;
      if (!nextArtifact) return tab;
      if (
        nextArtifact.id !== tab.artifact.id ||
        nextArtifact.demoUrl !== tab.artifact.demoUrl ||
        nextArtifact.content !== tab.artifact.content
      ) {
        changed = true;
        return { ...tab, artifact: nextArtifact };
      }
      return tab;
    });
    if (changed) setTabs(nextTabs);
  }, [messages, tabs]);

  /**
   * Return the active artifact's DB id, resolving it on demand when it hasn't
   * landed yet (the row is written by an async task). Looks the row up by the
   * owning message's `llmInteractionId` and adopts it.
   */
  const ensureArtifactId = useCallback(async (): Promise<string | null> => {
    const key = activeTabKeyRef.current;
    const tab = tabsRef.current.find((t) => t.key === key);
    const art = tab?.artifact;
    if (!art) return null;
    if (art.id) return art.id;

    const msgs = messagesRef.current;
    const sourceId = tab?.sourceMessageId ?? null;
    const source =
      (sourceId ? msgs.find((m) => m.id === sourceId) : undefined) ??
      msgs.find(
        (m) => m.artifact && m.artifact.name === art.name && m.artifact.content === art.content
      );
    const interactionId = (source as { llmInteractionId?: string } | undefined)?.llmInteractionId;
    if (!interactionId) return null;

    try {
      const rows = await artifactService.listArtifactsByLlmInteraction(interactionId);
      const row = rows[0];
      if (!row?.id) return null;
      updateActiveArtifact((prev) => ({
        ...prev,
        id: row.id,
        content: prev.content || row.content || '',
        structuredContent: prev.structuredContent ?? row.structured_content ?? null,
        demoUrl: prev.demoUrl ?? row.demo_url ?? null,
      }));
      return row.id;
    } catch {
      return null;
    }
  }, [updateActiveArtifact]);

  const notReadyToast = () =>
    showToast({
      message: 'Artifact is still finalizing — please try again in a moment.',
      severity: 'info',
    });

  // Save edits: persist to the API, reflect back into the active tab and the
  // message that owns the artifact.
  const save = useCallback(
    async (content: string) => {
      const art = tabsRef.current.find((t) => t.key === activeTabKeyRef.current)?.artifact;
      if (!art) return;
      const id = art.id ?? (await ensureArtifactId());
      if (!id) return notReadyToast();
      try {
        const updated = await artifactService.updateArtifact(id, { content });
        updateActiveArtifact((prev) => ({ ...prev, content: updated.content ?? content }));
        const targetMessage = conversationMessages.find((m) => m.artifact?.id === id);
        if (targetMessage) {
          dispatch(
            updateAIMessage({
              messageId: targetMessage.id,
              content: targetMessage.content,
              artifact: {
                ...targetMessage.artifact!,
                content: updated.content ?? content,
              },
            })
          );
        }
        showToast({ message: 'Artifact updated', severity: 'success' });
      } catch (err) {
        console.error('Failed to update artifact', err);
        showToast({ message: 'Failed to save artifact', severity: 'error' });
        throw err;
      }
    },
    [conversationMessages, dispatch, ensureArtifactId, updateActiveArtifact]
  );

  // Save manual edits to a structured (JSON) artifact — PUT updates the row in
  // place. Reflects the edited JSON back into the active tab and owning message.
  const saveStructured = useCallback(
    async (json: Record<string, unknown>) => {
      const art = tabsRef.current.find((t) => t.key === activeTabKeyRef.current)?.artifact;
      if (!art) return;
      const id = art.id ?? (await ensureArtifactId());
      if (!id) return notReadyToast();
      try {
        const updated = await artifactService.updateArtifact(id, { structured_content: json });
        const nextStructured = updated.structured_content ?? json;
        updateActiveArtifact((prev) => ({
          ...prev,
          structuredContent: nextStructured,
          content: updated.content ?? prev.content,
        }));
        const targetMessage = conversationMessages.find((m) => m.artifact?.id === id);
        if (targetMessage?.artifact) {
          dispatch(
            updateAIMessage({
              messageId: targetMessage.id,
              content: targetMessage.content,
              artifact: {
                ...targetMessage.artifact,
                structuredContent: nextStructured,
                content: updated.content ?? targetMessage.artifact.content,
              },
            })
          );
        }
        showToast({ message: 'Artifact updated', severity: 'success' });
      } catch (err) {
        console.error('Failed to update artifact', err);
        showToast({ message: 'Failed to save artifact', severity: 'error' });
        throw err;
      }
    },
    [conversationMessages, dispatch, ensureArtifactId, updateActiveArtifact]
  );

  // Publish the active artifact to S3 and copy the public link.
  const share = useCallback(async () => {
    const art = tabsRef.current.find((t) => t.key === activeTabKeyRef.current)?.artifact;
    if (!art) return;
    const id = art.id ?? (await ensureArtifactId());
    if (!id) return notReadyToast();
    try {
      const view = art.structuredContent
        ? resolveArtifact(art.structuredContent, art.name)
        : null;
      const html = view
        ? view.renderer.toHtml(view.model, composeArtifactTitle(art.structuredContent) ?? art.name)
        : renderArtifactHtml(art.content ?? '', art.name);
      const updated = await artifactService.publishArtifactToS3(id, html);
      const demoUrl = updated.demo_url ?? art.demoUrl ?? null;
      if (!demoUrl) throw new Error('Publish returned no URL');
      updateActiveArtifact((prev) => ({ ...prev, demoUrl }));
      await navigator.clipboard.writeText(demoUrl);
      showToast({ message: 'Share link copied to clipboard', severity: 'success' });
    } catch (err) {
      console.error('Failed to share artifact', err);
      showToast({ message: 'Failed to create share link', severity: 'error' });
      throw err;
    }
  }, [ensureArtifactId, updateActiveArtifact]);

  return {
    activeArtifact,
    isOpen,
    tabs,
    activeTabKey,
    open,
    close,
    closeTab,
    selectTab,
    save,
    saveStructured,
    share,
  };
}
